import { Button, type Icons, Span, Spinner } from "@virtual-brain-lib/frontend-design-system/ui";
import { capitalize } from "@virtual-brain-lib/frontend-utils";
import type { AccountProvider } from "@virtual-brain-lib/types";

import { useChatContext } from "../../../chat/core/context/ChatContext";
import type { MailboxConnectionState } from "./useMailboxConnection";

const MAILBOX_PRODUCT_NAMES: Record<AccountProvider, string> = {
	google: "Gmail",
	microsoft: "Outlook",
};

const DARK_BUTTON_CLASS_NAMES = {
	wrapper: "bg-default not-disabled:hover:bg-default/85 not-disabled:active:bg-default/75",
	text: "text-background [&_span]:text-background",
};

const COMPACT_SUMMARIES: Record<MailboxConnectionState, string> = {
	"not-connected": "Not connected",
	expired: "Connection expired",
	waiting: "Waiting for sign-in",
	closed: "Not connected",
	connected: "Connected",
};

const STATUS_ICONS: Record<MailboxConnectionState, Icons> = {
	"not-connected": "plug",
	expired: "plug",
	waiting: "spinner",
	closed: "plug",
	connected: "circleCheck",
};

export function getMailboxConnectionHeader(
	connectionState: MailboxConnectionState,
	provider: AccountProvider,
) {
	const productName = MAILBOX_PRODUCT_NAMES[provider];
	const statusTexts: Record<MailboxConnectionState, string> = {
		"not-connected": `${productName} not connected yet`,
		expired: `${productName} connection expired`,
		waiting: `Sign in to ${capitalize(provider)} in the window that opened`,
		closed: "Sign-in was not finished",
		connected: `${productName} connected`,
	};

	return {
		statusText: statusTexts[connectionState],
		compactSummary: COMPACT_SUMMARIES[connectionState],
		statusIcon: STATUS_ICONS[connectionState],
	};
}

interface ToolCardMailboxConnectProps {
	readonly connectionState: MailboxConnectionState;
	readonly provider: AccountProvider;
	readonly onConnect: () => void;
}

export function ToolCardMailboxConnect({
	connectionState,
	provider,
	onConnect,
}: ToolCardMailboxConnectProps) {
	const { type } = useChatContext();
	const productName = MAILBOX_PRODUCT_NAMES[provider];
	const descriptions: Record<MailboxConnectionState, string | undefined> = {
		"not-connected": `Your company has turned on email search. Connect your ${productName} account to include your emails. Read only.`,
		expired: "Connect again to include your emails.",
		waiting: undefined,
		closed: "Connect again whenever you are ready.",
		connected:
			type === "app"
				? "Ask for a new version of this block to include your emails."
				: "Ask your question again to include your emails.",
	};
	const description = descriptions[connectionState];
	const buttonLabel =
		connectionState === "expired" ? `Reconnect ${productName}` : `Connect ${productName}`;

	return (
		<div className="flex flex-col items-start gap-2">
			{description && (
				<Span variant="body-xs" color="default">
					{description}
				</Span>
			)}
			{connectionState === "waiting" && (
				<Button
					disabled
					size="sm"
					classNames={DARK_BUTTON_CLASS_NAMES}
					leftComponent={<Spinner color="white" size="xs" />}
				>
					Waiting for sign-in...
				</Button>
			)}
			{connectionState !== "waiting" && connectionState !== "connected" && (
				<Button
					size="sm"
					classNames={DARK_BUTTON_CLASS_NAMES}
					leftIcon={provider}
					onClick={onConnect}
				>
					{buttonLabel}
				</Button>
			)}
		</div>
	);
}
